import { Typography } from '@navi/web-ui/lib/primitives';
import styles from '../../AgentForm.module.scss';
import AgentFormTextbox from './AgentFormTextbox';
import { ENTITY_TYPE, VALIDATION_MESSAGE } from '../../constants/constants';
import AgentFormDropdown from './AgentFormDropdown';
import AgentFormAutocompleteDropdown from './AgentFormAutocompleteDropdown';
import {
  convertStringToDropdownOption,
  convertStringToDropdownOptionTitleCase,
  convertToDropdownOptions,
  convertToDropdownOptionsTitleCase,
  transformedRegion
} from '../../utils/utils';
import AgentFormDownloadDraCertificate from './AgentFormDownloadDraCertificate';
import AgentFormUploadControl from './AgentFormUploadControl';
import { IDefaultAgentFormProps } from '../../interfaces/types';
import { useSelector } from 'react-redux';
import { RootState } from '@cp/src/store';
import cx from 'classnames';
import AgentFormDateInput from './AgentFormDateInput';
import AgentFormDownloadPan from './AgentFormDownloadPan';
import ActiveNextMonthWrapper from './ActiveNextMonthWrapper';
import { useState } from 'react';
import CorporateNumbersDrawer from './CorporateNumbersDrawer';
import AgentBaseLocation from './AgentBaseLocation';
import AgentFormMultiSelectDropdown from './AgentFormMultiSelectDropdown';
import {
  handleLocationCoordinatesChange,
  handleVerification
} from '../../actions/agentFormActions';
import AgentAllocationDetails from './AgentAllocationDetails';

const DefaultAgentForm = (props: IDefaultAgentFormProps) => {
  const {
    control,
    errors,
    isEditDisabled,
    watch,
    isEmailRequired,
    validateEmail,
    isNaviEmailRequired,
    isEmailChangeDisabled,
    agentData,
    getValues,
    getAgencyCode,
    managerList,
    setValue,
    isDraUploadAccessFeatureFlagEnabled,
    setIsSubmitButtonDisabled,
    disableStateEdit,
    isAgentCenterRequired,
    isEditPanDetailsDisabled = false,
    isPanDetailsRequired = false,
    showActiveNextMonthToggle = false,
    isAgentActiveNextMonth,
    setIsAgentActiveNextMonth,
    isAgentActiveNextMonthToggleDirty,
    setIsAgentActiveNextMonthToggleDirty,
    hasRoleOMA = false,
    reverseGeocodedAgentCity,
    reverseGeocodedAgentPincode,
    locationVerificationStatus,
    setLocationVerificationStatus,
    showLocationVerificationToast,
    setShowLocationVerificationToast,
    setReverseGeocodedAgentCity,
    setReverseGeocodedAgentPincode,
    isPanFieldMandatory = true
  } = props;

  const bucketGroup = useSelector((state: RootState) => state?.teamLeadDashboard?.bucketGroup);
  const agentPreferences = useSelector(
    (state: RootState) => state.teamLeadDashboard.agentPreferences
  );

  const panVerificationStatus = agentData?.panCardView?.userDocument?.status || '';
  const [showVerifyButton, setShowVerifyButton] = useState(false);
  const [isPincodeMismatch, setIsPincodeMismatch] = useState(false);
  const [isMisMatchedPincode, setMisMatchedPincode] = useState(false);

  const [showCityPincode, setShowCityPincode] = useState(
    !!reverseGeocodedAgentCity && !!reverseGeocodedAgentPincode
  );

  const BUCKET_GROUPS = bucketGroup || [];
  const AGENT_REGIONS = agentPreferences?.regionList || [];
  const AGENT_STATES = agentPreferences?.stateList || [];
  const AGENT_LANGUAGES = agentPreferences?.languageList || [];
  const AGENT_CENTERS = agentPreferences?.agentCenters || [];

  const handlePincodeVerification = async () => {
    handleVerification({
      getValues,
      setLocationVerificationStatus,
      setShowLocationVerificationToast,
      setShowCityPincode,
      setReverseGeocodedAgentCity,
      setReverseGeocodedAgentPincode,
      setMisMatchedEntity: setMisMatchedPincode,
      entity: ENTITY_TYPE.PINCODE
    });
  };

  const handleAgentPincodeChange = (value: string) => {
    setMisMatchedPincode(false);
    if (
      reverseGeocodedAgentPincode &&
      value &&
      value !== reverseGeocodedAgentPincode &&
      value.length === 6
    ) {
      setIsPincodeMismatch(true);
    } else {
      setIsPincodeMismatch(false);
    }
  };

  const handleCoordinatesChange = (value: string) => {
    handleLocationCoordinatesChange({
      setIsEntityMismatch: setIsPincodeMismatch,
      setLocationVerificationStatus,
      setShowVerifyButton,
      setShowCityPincode,
      setShowLocationVerificationToast
    });
  };

  return (
    <>
      <div className={'pt-4 pl-5'}>
        <Typography variant={'h4'} color="var(--navi-color-gray-c2)">
          Agent Details
        </Typography>
      </div>
      {showActiveNextMonthToggle ? (
        <ActiveNextMonthWrapper
          showActiveNextMonthToggle={showActiveNextMonthToggle}
          isEditDisabled={isEditDisabled as boolean}
          isAgentActiveNextMonth={isAgentActiveNextMonth}
          setIsAgentActiveNextMonth={setIsAgentActiveNextMonth}
          isAgentActiveNextMonthToggleDirty={isAgentActiveNextMonthToggleDirty}
          setIsAgentActiveNextMonthToggleDirty={setIsAgentActiveNextMonthToggleDirty}
        />
      ) : null}
      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="agentName"
          rules={{ required: true }}
          errors={errors}
          isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
          styles={styles}
          inputLabel={'Agent Name'}
          placeholder={'Enter here'}
          validationMessage={VALIDATION_MESSAGE.AGENT_NAME_MESSAGE}
        />
      </div>

      {isPanDetailsRequired ? (
        <div className={styles.inputContainer}>
          <AgentFormDateInput
            control={control}
            name={'dateOfBirth'}
            rules={{ required: isPanFieldMandatory }}
            errors={errors}
            placeholder={'Select date'}
            inputLabel={'Date of birth (as per PAN)'}
            isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
            styles={styles}
            validationMessage={VALIDATION_MESSAGE.DATE_OF_BIRTH_MESSAGE}
            showRequiredIcon={isPanFieldMandatory}
            defaultSelected={null}
          />
        </div>
      ) : null}

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="employeeId"
          rules={{ required: getAgencyCode() === '1000' }}
          errors={errors}
          isEditDisabled={isEditDisabled}
          styles={styles}
          inputLabel={'Employee ID'}
          placeholder={'Enter here'}
          validationMessage={VALIDATION_MESSAGE.AGENT_EMPLOYEE_ID_MESSAGE}
          isRequired={getAgencyCode() === '1000'}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="emailAddress"
          rules={{
            required: isEmailRequired(),
            validate: (value: string) => {
              if (watch('emailAddress')) {
                return validateEmail(value, isNaviEmailRequired);
              }
            }
          }}
          isRequired={isEmailRequired()}
          inputLabel={'Email ID'}
          placeholder={isEditDisabled && agentData.emailAddress === null ? 'NA' : 'Enter here'}
          isEditDisabled={isEditDisabled || isEmailChangeDisabled}
          errors={errors}
          styles={styles}
          validationMessage={
            isNaviEmailRequired
              ? VALIDATION_MESSAGE.NAVI_EMAIL_MESSAGE
              : VALIDATION_MESSAGE.EMAIL_MESSAGE
          }
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormTextbox
          control={control}
          name="phoneNumber"
          rules={{
            required: true,
            minLength: 10,
            maxLength: 10,
            pattern: /^[0-9]{10}$/i
          }}
          inputLabel={'Phone Number'}
          placeholder={'Enter here'}
          isEditDisabled={isEditDisabled}
          errors={errors}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.PHONE_NUMBER_MESSAGE}
        />
      </div>

      <CorporateNumbersDrawer isEditDisabled={isEditDisabled} agentData={agentData} />

      {isPanDetailsRequired ? (
        <div className={styles.inputContainer}>
          <AgentFormTextbox
            control={control}
            name="panNumber"
            rules={{
              required: isPanDetailsRequired && isPanFieldMandatory,
              pattern: /^[a-zA-Z0-9]{10}$/
            }}
            errors={errors}
            isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
            styles={styles}
            inputLabel={'PAN'}
            placeholder={'Enter PAN'}
            validationMessage={VALIDATION_MESSAGE.PAN_CARD_MESSAGE}
            isRequired={isPanDetailsRequired && isPanFieldMandatory}
            isUpperCaseRequired={true}
          />
        </div>
      ) : null}

      {isPanDetailsRequired ? (
        watch('panCardView') ? (
          <div className="w-[364px] ml-2 mt-4">
            <AgentFormDownloadPan
              control={control}
              name={'panCardView'}
              rules={{ required: isPanDetailsRequired && isPanFieldMandatory }}
              isEditDisable={isEditDisabled || isEditPanDetailsDisabled}
              inputLabel={'PAN card'}
              panVerificationStatus={panVerificationStatus}
              isRequired={isPanDetailsRequired && isPanFieldMandatory}
            />
          </div>
        ) : (
          <AgentFormUploadControl
            control={control}
            name={'panCard'}
            rules={{ required: isPanDetailsRequired && isPanFieldMandatory }}
            errors={errors}
            isEditDisabled={isEditDisabled || isEditPanDetailsDisabled}
            styles={{}}
            setSubmitButtonDisabled={setIsSubmitButtonDisabled}
            inputLabel={'PAN card'}
            documentType={'PAN_CARD'}
            documentContentType={'JPG'}
            supportedFile={'.jpeg, .jpg, .png'}
            isRequired={isPanDetailsRequired && isPanFieldMandatory}
          />
        )
      ) : null}

      <div className={styles.inputContainer}>
        <AgentFormDropdown
          control={control}
          name={'bucketGroup'}
          rules={{ required: true }}
          errors={errors}
          placeholder={'Select bucket group'}
          inputLabel={'Bucket Group'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.BUCKET_GROUP_MESSAGE}
          options={convertToDropdownOptions(BUCKET_GROUPS)}
          defaultSelected={convertStringToDropdownOption(getValues('bucketGroup'))}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'reportingManagerEmailAddress'}
          inputLabel={'Reporting Manager'}
          rules={{ required: true }}
          errors={errors}
          placeholder={'Type or Select reporting manager'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          options={managerList}
          validationMessage={VALIDATION_MESSAGE.REPORTING_MANAGER_MESSAGE}
          updateClearAllCallback={() => {
            setValue('reportingManagerEmailAddress', '');
          }}
        />
      </div>

      {hasRoleOMA && (
        <>
          <AgentBaseLocation
            latitude={'currentMonthLatitude'}
            longitude={'currentMonthLongitude'}
            control={control}
            errors={errors}
            isEditDisabled={true}
            callbackFn={handleCoordinatesChange}
          />
          <div className={styles.locationContainer}>
            <div className="flex">
              <Typography variant="p4" color="var(--navi-color-gray-c2)" className="pr-1">
                {'Agent Pincode '}
              </Typography>
              {
                <Typography variant="p4" color="var(--navi-color-gray-c3)">
                  {'(for current month)'}
                </Typography>
              }
            </div>
            <div>
              <AgentFormTextbox
                control={control}
                name="currentMonthAgentPincode"
                rules={{
                  required: false
                }}
                inputLabel={''}
                placeholder={'Enter 6-digit pincode'}
                isEditDisabled={true}
                errors={errors}
                styles={styles}
                validationMessage={VALIDATION_MESSAGE.PHONE_NUMBER_MESSAGE}
              />
            </div>
          </div>
        </>
      )}

      <div className={'pt-7 pl-5'}>
        <Typography variant={'h4'} color="var(--navi-color-gray-c2)">
          Agent language/region
        </Typography>
      </div>

      <div className={cx(styles.inputContainer, '!pt-[16px]')}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'primaryRegion'}
          inputLabel={'Primary Region'}
          rules={{ required: isAgentCenterRequired }}
          showRequiredIcon={isAgentCenterRequired}
          errors={errors}
          placeholder={'Type or Select primary region'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.PRIMARY_REGION_MESSAGE}
          options={transformedRegion(AGENT_REGIONS).filter(
            region => ![watch('tertiaryRegion'), watch('secondaryRegion')].includes(region.value)
          )}
          updateClearAllCallback={() => {
            setValue('primaryRegion', '');
          }}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'secondaryRegion'}
          inputLabel={'Secondary Region'}
          rules={{ required: false }}
          showRequiredIcon={false}
          errors={errors}
          placeholder={'Type or Select secondary region'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          options={transformedRegion(AGENT_REGIONS).filter(
            region => ![watch('primaryRegion'), watch('tertiaryRegion')].includes(region.value)
          )}
          updateClearAllCallback={() => {
            setValue('secondaryRegion', '');
          }}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'tertiaryRegion'}
          inputLabel={'Tertiary Region'}
          rules={{ required: false }}
          showRequiredIcon={false}
          errors={errors}
          placeholder={'Type or Select tertiary region'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          options={transformedRegion(AGENT_REGIONS).filter(
            region => ![watch('primaryRegion'), watch('secondaryRegion')].includes(region.value)
          )}
          updateClearAllCallback={() => {
            setValue('tertiaryRegion', '');
          }}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'state'}
          inputLabel={'Agent State'}
          rules={{ required: true }}
          errors={errors}
          placeholder={'Type or Select state'}
          isEditDisabled={isEditDisabled || disableStateEdit}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.AGENT_STATE_MESSAGE}
          options={convertToDropdownOptionsTitleCase(AGENT_STATES)}
          updateClearAllCallback={() => {
            setValue('state', '');
          }}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormDropdown
          control={control}
          name={'agentCenter'}
          rules={{ required: isAgentCenterRequired }}
          errors={errors}
          placeholder={'Select agent center'}
          inputLabel={'Agent Center'}
          isEditDisabled={isEditDisabled}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.AGENCY_CENTRE_MESSAGE}
          options={convertToDropdownOptionsTitleCase(AGENT_CENTERS)}
          defaultSelected={convertStringToDropdownOptionTitleCase(getValues('agentCenter') ?? '')}
          showRequiredIcon={isAgentCenterRequired}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'primaryLanguage'}
          inputLabel={'Primary Language'}
          rules={{ required: true }}
          errors={errors}
          placeholder={'Type or Select language'}
          isEditDisabled={isEditDisabled || disableStateEdit}
          styles={styles}
          validationMessage={VALIDATION_MESSAGE.PRIMARY_LANGUAGE_MESSAGE}
          options={convertToDropdownOptionsTitleCase(AGENT_LANGUAGES)}
          updateClearAllCallback={() => {
            setValue('primaryLanguage', '');
          }}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'secondaryLanguage'}
          inputLabel={'Secondary Language'}
          rules={{ required: false }}
          errors={errors}
          placeholder={'Type or Select language'}
          isEditDisabled={isEditDisabled || disableStateEdit}
          styles={styles}
          options={convertToDropdownOptionsTitleCase(AGENT_LANGUAGES)}
          updateClearAllCallback={() => {
            setValue('secondaryLanguage', '');
          }}
          showRequiredIcon={false}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormAutocompleteDropdown
          control={control}
          name={'tertiaryLanguage'}
          inputLabel={'Tertiary Language'}
          rules={{ required: false }}
          errors={errors}
          placeholder={'Type or Select language'}
          isEditDisabled={isEditDisabled || disableStateEdit}
          styles={styles}
          options={convertToDropdownOptionsTitleCase(AGENT_LANGUAGES)}
          updateClearAllCallback={() => {
            setValue('tertiaryLanguage', '');
          }}
          showRequiredIcon={false}
        />
      </div>

      <div className={styles.inputContainer}>
        <AgentFormMultiSelectDropdown
          control={control}
          name={'extraLanguages'}
          inputLabel={'Extra Languages'}
          errors={errors}
          placeholder={'Select languages'}
          isEditDisabled={isEditDisabled || disableStateEdit}
          styles={styles}
          options={convertToDropdownOptionsTitleCase(AGENT_LANGUAGES).filter(
            lang =>
              ![
                watch('primaryLanguage'),
                watch('secondaryLanguage'),
                watch('tertiaryLanguage')
              ].some(selected => selected?.toLowerCase() === lang.value?.toLowerCase())
          )}
          showRequiredIcon={false}
        />
      </div>
      {watch('draCertificateView') ? (
        <div className="w-[364px] ml-2 mt-4">
          <AgentFormDownloadDraCertificate
            control={control}
            name={'draCertificateView'}
            rules={{ required: false }}
            isEditDisable={isEditDisabled || !isDraUploadAccessFeatureFlagEnabled}
          />
        </div>
      ) : (
        <div>
          {isDraUploadAccessFeatureFlagEnabled && !isEditDisabled ? (
            <AgentFormUploadControl
              control={control}
              name={'draCertificate'}
              rules={{ required: false }}
              errors={errors}
              isEditDisabled={false}
              styles={{}}
              setSubmitButtonDisabled={setIsSubmitButtonDisabled}
            />
          ) : null}
        </div>
      )}

      {hasRoleOMA && (
        <AgentAllocationDetails
          control={control}
          errors={errors}
          agentData={agentData}
          getValues={getValues}
          locationVerificationStatus={locationVerificationStatus}
          showLocationVerificationToast={showLocationVerificationToast}
          isEditDisabled={isEditDisabled}
          handleAgentPincodeChange={handleAgentPincodeChange}
          handleCoordinatesChange={handleCoordinatesChange}
          isPincodeMismatch={isPincodeMismatch}
          reverseGeocodedAgentCity={reverseGeocodedAgentCity}
          reverseGeocodedAgentPincode={reverseGeocodedAgentPincode}
          handlePincodeVerification={handlePincodeVerification}
          showVerifyButton={showVerifyButton}
          showCityPincode={showCityPincode}
          isMisMatchedPincode={isMisMatchedPincode}
        />
      )}
    </>
  );
};

export default DefaultAgentForm;
